import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import useConfig from 'hooks/useConfig';
import useApi from 'hooks/useApi';
import Cookies from 'universal-cookie';
import { ThemeMode } from 'config';
import AuthWrapper1 from '../authentication/AuthWrapper1';
import WelcomeBanner from '../authentication/WelcomeBanner';
import SignupLogo from 'ui-component/SignupLogo';
import AccountContextListItem from 'components/accountContext/AccountContextListItem';
import {
  extractLiveAccountContexts,
  filterAccountContexts,
  loadCachedAccountContexts,
  loadPostLoginPending,
  persistEnrichedLiveContexts,
} from 'utils/accountContext/accountContextUtils';
import {
  buildSwitchLoginPayload,
  continueWithAccountContext,
} from 'utils/accountContext/performAccountSwitch';
import { useOrganization } from 'contexts/OrganizationContext';

const cookies = new Cookies();

export default function AccountSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const theme = useTheme();
  const isDark = theme.palette.mode === ThemeMode.DARK;
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const { hideLoader, showLoader } = useConfig();
  const { updateOrganizationContext, refreshHierarchyTree } = useOrganization();
  const [submittingId, setSubmittingId] = useState('');
  const [contexts, setContexts] = useState(() => loadCachedAccountContexts(location.state));
  const [refreshing, setRefreshing] = useState(true);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [accessRevoked, setAccessRevoked] = useState(Boolean(location.state?.accessRevoked));
  const [searchText, setSearchText] = useState('');

  const pendingLogin = useMemo(() => loadPostLoginPending(), []);
  const switchMode = Boolean(location.state?.switchMode);
  const myInsightTitle = intl.formatMessage({ id: 'myInsightAccount', defaultMessage: 'My Insight Account' });
  const filteredContexts = useMemo(
    () => filterAccountContexts(contexts, searchText, { myInsightTitle }),
    [contexts, searchText, myInsightTitle]
  );
  const submitting = Boolean(submittingId);

  useEffect(() => {
    hideLoader();
    let cancelled = false;

    const refreshLiveContexts = async () => {
      setRefreshing(true);
      try {
        const snapshot = pendingLogin;
        const userMongoId = cookies.get('_Id') || snapshot?.data?._id;
        if (userMongoId && !cookies.get('_Id')) {
          cookies.set('_Id', userMongoId, {
            secure: Boolean(process.env.secureCoockie),
            path: '/',
          });
        }
        if (!userMongoId) {
          setRefreshFailed(true);
          return;
        }
        const result = await useApi('GET_ACCOUNT_CONTEXTS');
        if (cancelled) return;
        const { ok, contexts: liveContexts } = extractLiveAccountContexts(result);
        if (!ok) {
          setRefreshFailed(true);
          return;
        }
        setContexts(persistEnrichedLiveContexts(liveContexts));
        setRefreshFailed(false);
      } catch {
        if (!cancelled) {
          setRefreshFailed(true);
        }
      } finally {
        if (!cancelled) {
          setRefreshing(false);
        }
      }
    };

    refreshLiveContexts();
    return () => {
      cancelled = true;
    };
    // Fetch once when the picker opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only live refresh
  }, []);

  useEffect(() => {
    if (refreshing) return;
    if (!contexts.length) {
      navigate('/', { replace: true });
    }
  }, [contexts, navigate, refreshing]);

  const handleSelect = async (selected) => {
    if (refreshing || submitting || !selected) return;
    const loginPayload = pendingLogin || (switchMode ? buildSwitchLoginPayload() : null);
    if (!loginPayload) return;

    setSubmittingId(selected.id);
    const switchingMessage = intl.formatMessage({
      id: 'switchingAccount',
      defaultMessage: 'Switching your account…',
    });
    showLoader(switchMode ? switchingMessage : undefined);

    try {
      const result = await continueWithAccountContext({
        selected,
        loginPayload,
        switchMode,
        navigate,
        updateOrganizationContext,
        refreshHierarchyTree,
        allOrganizationsLabel: intl.formatMessage({ id: 'allOrganizations', defaultMessage: 'All Organizations' }),
        onManagerAccessRevoked: (live) => {
          setAccessRevoked(true);
          setContexts(live);
        },
      });

      // Stay on picker for revoked / empty-org manager cases (!result.ok).
    } finally {
      setSubmittingId('');
      hideLoader();
    }
  };

  if (!contexts.length) {
    return null;
  }

  const pageTitle = switchMode
    ? intl.formatMessage({ id: 'switchAccountsTitle', defaultMessage: 'Switch Accounts' })
    : intl.formatMessage({ id: 'accountSelectionTitle', defaultMessage: 'Select Account' });

  const pageSubtitle = switchMode
    ? intl.formatMessage({
        id: 'accountSelectionSwitchSubtitle',
        defaultMessage: 'Choose which account you want to use.',
      })
    : intl.formatMessage({
        id: 'accountSelectionSubtitle',
        defaultMessage: 'Your User ID has access to multiple Insight accounts. Choose one to continue.',
      });

  return (
    <AuthWrapper1>
      <Grid
        container
        sx={{ height: { xs: '100dvh', md: '100vh' }, overflow: 'hidden', m: 0 }}
      >
        <Grid
          size={{ xs: 12, md: 5 }}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            py: { xs: 1, md: 0 },
          }}
        >
          <Box
            sx={{
              mt: { xs: 1, md: 4 },
              mb: 0,
              ml: { xs: 0, md: 6 },
              px: { xs: 2, md: 0 },
              textAlign: { xs: 'center', md: 'left' },
              flexShrink: 0,
            }}
          >
            <SignupLogo />
          </Box>

          {/* Narrow centered column — matches Login / Figma side gutters */}
          <Box
            sx={{
              maxWidth: 320,
              width: '100%',
              mx: 'auto',
              mt: { xs: 5, md: 8 },
              px: { xs: 2, md: 0 },
              pb: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              flex: 1,
              minHeight: 0,
              '@media (min-width: 900px) and (max-width: 980px)': {
                maxWidth: 280,
              },
            }}
          >
            <Stack spacing={1} sx={{ alignItems: 'flex-start', textAlign: 'left', width: '100%', mb: 2.5, flexShrink: 0 }}>
              <Typography
                component="h1"
                sx={{
                  color: isDark ? 'text.primary' : '#212121',
                  fontWeight: 600,
                  fontSize: downMD ? '22px' : '24px',
                  lineHeight: 1.3,
                  letterSpacing: 0,
                }}
              >
                {pageTitle}
              </Typography>
              <Typography
                sx={{
                  color: isDark ? 'text.secondary' : '#757575',
                  fontWeight: 400,
                  fontSize: '13px',
                  lineHeight: 1.5,
                }}
              >
                {pageSubtitle}
              </Typography>
            </Stack>

            {accessRevoked && (
              <Alert severity="info" sx={{ width: '100%', mb: 1.5, flexShrink: 0 }}>
                <FormattedMessage
                  id="accountSelectionAccessRevoked"
                  defaultMessage="You no longer have access to that admin account. Choose another account."
                />
              </Alert>
            )}

            {refreshFailed && (
              <Alert severity="warning" sx={{ width: '100%', mb: 1.5, flexShrink: 0 }}>
                <FormattedMessage
                  id="accountSelectionRefreshFailed"
                  defaultMessage="We could not refresh your accounts. Showing the last list we have. Open the account menu again if an account is missing."
                />
              </Alert>
            )}

            <TextField
              size="small"
              fullWidth
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder={intl.formatMessage({
                id: 'accountSelectionSearchPlaceholder',
                defaultMessage: 'Search accounts',
              })}
              inputProps={{
                'aria-label': intl.formatMessage({
                  id: 'accountSelectionSearchPlaceholder',
                  defaultMessage: 'Search accounts',
                }),
                'data-testid': 'account-selection-search',
              }}
              sx={{
                mb: 2,
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  height: 40,
                  borderRadius: '6px',
                  bgcolor: isDark ? 'dark.800' : 'common.white',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: isDark ? undefined : '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderWidth: '1px',
                    borderColor: isDark ? undefined : '#BDBDBD',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '1px',
                    borderColor: isDark ? undefined : '#BDBDBD',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: 13,
                  py: 1,
                  color: isDark ? undefined : '#616161',
                  '&::placeholder': {
                    color: '#9E9E9E',
                    opacity: 1,
                  },
                },
                '& .MuiInputAdornment-root': {
                  color: isDark ? 'text.secondary' : '#9E9E9E',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={intl.formatMessage({ id: 'clear', defaultMessage: 'Clear' })}
                      onClick={() => setSearchText('')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {refreshing ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flex: 1, minHeight: 0 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 0.25,
                  mb: 1.5,
                  WebkitOverflowScrolling: 'touch',
                }}
                data-testid="account-selection-list"
              >
                {filteredContexts.length === 0 ? (
                  <Typography
                    sx={{
                      color: isDark ? 'text.secondary' : '#757575',
                      textAlign: 'left',
                      py: 4,
                      fontSize: 13,
                    }}
                  >
                    <FormattedMessage
                      id="accountSelectionNoMatches"
                      defaultMessage="No accounts match your search."
                    />
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {filteredContexts.map((context) => (
                      <AccountContextListItem
                        key={context.id}
                        context={context}
                        intl={intl}
                        showChevron
                        disabled={submitting}
                        selected={submittingId === context.id}
                        onSelect={() => handleSelect(context)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid size={7} sx={{ display: { xs: 'none', md: 'inline-flex' }, height: '100%', overflow: 'hidden' }}>
          <WelcomeBanner />
        </Grid>
      </Grid>
    </AuthWrapper1>
  );
}
